import { Commodity, NormalizedLoad } from '../../../../types/Load';
import { Undef } from '../../../../types/UtilityTypes';
import { normalizeRevenovaItemNumber } from './commodityItemNumber';
import { hasCommodityData } from './utils/commodityCalculations';

// An untouched item row would reach the TMS as an empty line item that displaces the load's
// weight and pallet totals.
export function isBlankRevenovaCommodity(commodity: Commodity): boolean {
  return (
    !hasCommodityData(commodity) &&
    !commodity.description?.trim() &&
    !commodity.additionalMarkings?.trim()
  );
}

// Keep item and handling counts independent when mapping form fields back to the API.
export function normalizeCommodityForRevenovaAPI(
  commodity: Commodity
): Omit<Commodity, 'additionalMarkings'> {
  const { additionalMarkings, ...fields } = commodity;
  const { itemNumber, itemDescription } = normalizeRevenovaItemNumber(
    commodity.description || '',
    additionalMarkings || ''
  );
  return {
    ...fields,
    referenceNumber: itemNumber,
    description: itemDescription,
    quantity: Number.isFinite(commodity.quantity) ? commodity.quantity : 0,
    handlingQuantity: Number.isFinite(commodity.handlingQuantity)
      ? commodity.handlingQuantity
      : 0,
  };
}

// Helper to normalize commodities for Revenova UI (mapping API fields to form fields)
export function normalizeCommoditiesForRevenovaUI(
  commodities: Commodity[],
  baseLoad: NormalizedLoad
): Commodity[] {
  return commodities.map((commodity) => {
    const normalizeUnit = (unit: any) => {
      if (
        unit === '' ||
        unit === null ||
        (typeof unit === 'string' && unit.trim() === '')
      ) {
        return undefined;
      }
      return unit;
    };

    // Map weight unit from grossWeight.unit, netWeight.unit, or specifications.totalWeight.unit
    // Priority: grossWeight.unit > netWeight.unit > specifications.totalWeight.unit > undefined
    let weightUnit: Undef<string> = undefined;
    if (commodity.grossWeight?.unit) {
      weightUnit = String(commodity.grossWeight.unit);
    } else if (commodity.netWeight?.unit) {
      weightUnit = String(commodity.netWeight.unit);
    } else if (baseLoad.specifications?.totalWeight?.unit) {
      weightUnit = String(baseLoad.specifications.totalWeight.unit);
    }

    // Normalize weightUnit - convert empty strings to undefined
    weightUnit = normalizeUnit(weightUnit);

    const normalized = {
      ...commodity,
      // Map referenceNumber to description (name field in UI)
      // Map description to additionalMarkings (description field in UI)
      // Use referenceNumber exclusively for description, don't fallback to description to avoid duplication
      description: commodity.referenceNumber || '',
      // Use description exclusively for additionalMarkings, fallback to additionalMarkings if description not present
      additionalMarkings:
        commodity.description || commodity.additionalMarkings || '',
      weightUnit,
      totalPieces: commodity.totalPieces
        ? {
            ...commodity.totalPieces,
            unit: normalizeUnit(commodity.totalPieces.unit),
          }
        : undefined,
      grossWeight: commodity.grossWeight
        ? {
            ...commodity.grossWeight,
            unit: normalizeUnit(commodity.grossWeight.unit),
          }
        : undefined,
      netWeight: commodity.netWeight
        ? {
            ...commodity.netWeight,
            unit: normalizeUnit(commodity.netWeight.unit),
          }
        : undefined,
    };

    // Note: Unit enum values are never empty strings, so no need to check for empty strings
    return normalized;
  });
}
